import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight, Bot, Check, ChevronLeft, ChevronRight, Coins, Copy, CornerDownRight, ExternalLink, Eye, Globe,
  HelpCircle, Instagram, MessageSquare, MessagesSquare, RotateCcw, Send, ThumbsDown, ThumbsUp, X,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, dateCourte } from "./api";
import TexteAssistant from "../components/TexteAssistant";
import { ATonAttention, Badge, BoutonSombre, Carte, CarteIcone, Cellule, ChampChoix, Modale, Puces, Recherche, Section, Tableau, pointsATraiter } from "./ui";
import { Encart, Repartition } from "./DashboardPlus";

/* L'assistant du site (remplace FastBots) vu de l'école : statistiques, conversations,
   consignes (avec versions), bac à sable, et le texte exact qu'il lit avant de répondre.
   Il ne connaît que le site public : jamais les élèves, familles ou factures.
   Le même assistant est prêt pour les messages privés Instagram (réglages dans
   « Réglages & consignes » → Instagram) : chaque conversation montre d'où elle vient. */

/* Quatre onglets (simplifié le 12.10.2026, accord de l'école) : « Ce qu'il sait » est dans
   Consignes, « Évaluation » est devenue « Test complet » dans Tester. */
const VUES = [
  { valeur: "stats", label: "Statistiques" },
  { valeur: "conversations", label: "Conversations" },
  { valeur: "consignes", label: "Consignes" },
  { valeur: "tester", label: "Tester" },
];
const EFFORTS = [
  { valeur: "low", label: "Rapide (recommandé pour un site)" },
  { valeur: "medium", label: "Moyen" },
  { valeur: "high", label: "Approfondi (plus lent, plus cher)" },
];
/* Sur le site : en ligne ou en pause. FastBots n'existe plus (abonnement arrêté le 13.10.2026). */
const MODES_SITE = [
  { valeur: "maison", label: "En ligne" },
  { valeur: "aucun", label: "En pause" },
];
const CANAUX = [
  { valeur: "", label: "Tous" },
  { valeur: "site", label: "Site" },
  { valeur: "instagram", label: "Instagram" },
];
/* « Répond » ou « En pause ». « Observation » (comparer avec FastBots) ne se choisit plus ;
   il reste affiché seulement si c'est le réglage en place (version de test). */
const MODES_IG = {
  actif: { label: "Répond", ton: "vert", aide: "L'assistant répond lui-même aux messages privés Instagram." },
  eteint: { label: "En pause", ton: "ambre", aide: "Les messages arrivent toujours ici, mais l'assistant ne répond pas : l'équipe répond depuis Instagram." },
  observation: { label: "Observation", ton: "bleu", aide: "L'assistant écrit la réponse qu'il aurait envoyée, sans rien envoyer." },
};
const dollars = (n) => `${(n || 0).toFixed(2)} $`;
const heure = (iso) => (iso ? new Date(iso).toLocaleString("fr-CH", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
/* Format suisse : 09.10.2026 et 10:11. */
const dateCH = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const heureCH = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" }) : "");
/* Le drapeau d'un pays (« CH » → 🇨🇭) ; certains Windows l'affichent en lettres « CH ». */
const drapeau = (code) => (/^[A-Z]{2}$/.test(code || "")
  ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "");
const nombre = (n) => (n || 0).toLocaleString("fr-CH");
/* Une réponse de l'assistant en texte simple, pour une ligne de liste : sans **gras**, sans
   [liens](…) ni puces. */
const texteSimple = (t) => String(t || "").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
  .replace(/\*\*|__|`/g, "").replace(/^\s*[-*•]\s+/gm, "").replace(/\s+/g, " ").trim();

export default function Assistant({ aTraiter, cible }) {
  const [vue, setVue] = useState("stats");
  // « À ton attention » mène à Conversations (une personne attend l'équipe) ou aux réglages
  // Instagram (jeton) : la clé rouvre Consignes sur le bon onglet.
  const [consignes, setConsignes] = useState({ cle: 0, onglet: "site" });
  // « Questions sans réponse » (À traiter) rouvre Conversations sur le filtre « Sans réponse ».
  // Une ligne d'À traiter (« sans_reponse:<id> ») ouvre aussi la conversation elle-même.
  // Sinon, la liste derrière une conversation ouverte depuis les Statistiques reste sur
  // « L'assistant a répondu », comme à l'ouverture de l'onglet : avec « Toutes », on
  // retombait sur les messages Instagram qu'il n'a pas touchés (école, 30.10.2026).
  const [convs, setConvs] = useState({ cle: 0, filtre: "avec_reponse", ouvrir: "" });
  const voirConversation = (id = "", filtre = "avec_reponse") => {
    setConvs((c) => ({ cle: c.cle + 1, filtre, ouvrir: id }));
    setVue("conversations");
  };
  const voirSansReponse = (id = "") => voirConversation(id, "sans_reponse");
  useEffect(() => {
    const [vueCible, id] = String(cible || "").split(":");
    if (vueCible === "sans_reponse") voirSansReponse(id || "");
  }, [cible]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ecran, setEcran] = useState(null);
  const [ecranIg, setEcranIg] = useState(null);
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => {
    api("/chatbot").then(setEcran).catch((e) => setErreur(e.message));
    api("/chatbot/instagram").then(setEcranIg).catch(() => {});
  }, []);
  useEffect(charger, [charger]);

  return (
    <div className="space-y-6" data-testid="console-assistant">
      {/* L'en-tête aux couleurs de la section « Assistant IA » du tableau de bord. */}
      <div className="rounded-3xl border px-5 md:px-6 py-5 flex flex-wrap items-center gap-4"
        style={{ background: "#EEF2FF", borderColor: "#C7D2FE" }}>
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#4338CA", color: "#fff" }}>
          <Bot size={24} />
        </span>
        <div className="min-w-[14rem] flex-1">
          <h1 className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "#0F1B2D" }}>Assistant IA</h1>
          <p className="text-sm mt-1" style={{ color: "#3730A3" }}>
            Il répond aux visiteurs du site et aux messages privés Instagram, à partir du site public uniquement :
            le planning lu en direct, les tarifs, dates et stages des Réglages, et les pages du site. Il ne voit jamais la console.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {ecran && <Etat ecran={ecran} />}
          {ecranIg && (
            <Badge ton={(MODES_IG[ecranIg.reglages.instagram_mode] || MODES_IG.eteint).ton} testid="assistant-etat-instagram">
              <Instagram size={12} /> Instagram : {(MODES_IG[ecranIg.reglages.instagram_mode] || MODES_IG.eteint).label.toLowerCase()}
            </Badge>
          )}
        </div>
      </div>
      {erreur && <p className="text-sm text-red-700">{erreur}</p>}
      <ATonAttention testid="assistant-attention" points={pointsATraiter(aTraiter, ["assistant"], (g) => {
        if (g.id === "instagram_equipe") setVue("conversations");
        else if (g.id === "assistant_sans_reponse") voirSansReponse();
        else { setConsignes((c) => ({ cle: c.cle + 1, onglet: "instagram" })); setVue("consignes"); }
      })} />
      <Puces options={VUES} valeur={vue} onChange={setVue} testid="assistant-vues" />
      {vue === "stats" && <Stats onVoirConversations={() => voirConversation()} onOuvrir={voirConversation} />}
      {vue === "conversations" && <Conversations key={convs.cle} filtreInitial={convs.filtre} ouvrirId={convs.ouvrir} />}
      {vue === "consignes" && ecran && (
        <Consignes key={consignes.cle} initial={consignes.onglet} ecran={ecran} onEnregistre={setEcran}
          ecranIg={ecranIg} onEnregistreIg={setEcranIg} />
      )}
      {vue === "tester" && ecran && <Tester ecran={ecran} />}
    </div>
  );
}

function Etat({ ecran }) {
  const { effectif, reglages, cle_presente: cle } = ecran;
  if (effectif === "maison") {
    return <Badge ton="vert" testid="assistant-etat">En ligne sur le site</Badge>;
  }
  if (reglages.fournisseur === "maison" && !cle) {
    return (
      <div className="rounded-2xl border p-4 text-sm" style={{ background: "#FFFBEB", borderColor: "#FDE68A" }} data-testid="assistant-etat">
        <b>Il manque la clé Anthropic.</b> Ajoutez <code>ANTHROPIC_API_KEY</code> dans Render (Environment) :
        tant qu'elle manque, l'assistant n'apparaît pas sur le site.
      </div>
    );
  }
  return (
    <Badge ton="ambre" testid="assistant-etat">
      Site : en pause
      {!cle && " · clé Anthropic absente"}
    </Badge>
  );
}

/* Les couleurs des deux canaux, les mêmes que sur le tableau de bord. */
const COULEUR_SITE = "#4338CA";
const COULEUR_INSTAGRAM = "#DB2777";
const ETIQUETTE_FILTRE = "text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]";
const ilYa = (iso) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(min)) return "";
  if (min < 60) return `il y a ${Math.max(min, 1)} min`;
  if (min < 24 * 60) return `il y a ${Math.round(min / 60)} h`;
  return dateCourte(iso);
};

/* Statistiques (refaites le 12.10.2026, « le graphique prend trop de place et on ne voit pas
   les conversations ») : les chiffres en couleur, puis ce que les gens demandent — juste
   sous les chiffres —, puis un graphique compact (site et Instagram) à côté de ce que
   l'assistant fait des messages Instagram et d'où l'on écrit. */
/* Une question des listes : un clic ouvre toute la conversation, réponses de l'assistant
   comprises (école, 26.10.2026). */
const QuestionCliquable = ({ id, onOuvrir, className, style, children, testid }) => (id && onOuvrir ? (
  <button type="button" onClick={onOuvrir} data-testid={testid} title="Voir toute la conversation"
    className={`${className} text-left transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2`} style={style}>
    {children}
  </button>
) : <span className={className} style={style}>{children}</span>);

function Stats({ onVoirConversations, onOuvrir }) {
  const [jours, setJours] = useState(30);
  const [canal, setCanal] = useState("");
  const [s, setS] = useState(null);
  useEffect(() => { api(`/chatbot/stats?jours=${jours}&canal=${canal}`).then(setS).catch(() => {}); }, [jours, canal]);
  if (!s) return <p className="text-sm text-[var(--muted)]">Chargement…</p>;
  const avis = s.pouce_haut + s.pouce_bas;
  const ig = s.instagram || { recus: 0, reponses: 0, propositions: 0 };
  const montrerIg = canal === "instagram" || (canal === "" && ig.recus > 0);
  const dernieres = (s.dernieres || []).slice(0, 8);
  return (
    <div className="space-y-5" data-testid="assistant-stats">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-white border px-4 py-3"
        style={{ borderColor: "var(--border)" }}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={ETIQUETTE_FILTRE}>Période</span>
          <Puces options={[7, 30, 90, 365].map((j) => ({ valeur: j, label: `${j} jours` }))}
            valeur={jours} onChange={setJours} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={ETIQUETTE_FILTRE}>Canal</span>
          <Puces options={CANAUX.map((c) => ({ ...c, nb: c.valeur ? (s.par_canal || {})[c.valeur] : undefined }))}
            valeur={canal} onChange={setCanal} testid="assistant-stats-canal" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <CarteIcone titre="Conversations" valeur={nombre(s.conversations)} Icon={MessagesSquare} teinte="indigo"
          detail={`${nombre(s.visiteurs)} visiteurs différents`} testid="assistant-stat-conversations" />
        <CarteIcone titre="Questions" valeur={nombre(s.questions)} Icon={MessageSquare} teinte="bleu"
          detail={`${s.questions_par_conversation} par conversation`} testid="assistant-stat-questions" />
        <CarteIcone titre="Sans réponse" valeur={s.sans_reponse.length} Icon={HelpCircle}
          teinte={s.sans_reponse.length ? "ambre" : "vert"} detail="L'assistant n'avait pas l'information"
          testid="assistant-stat-sans-reponse" />
        <CarteIcone titre="Avis" valeur={avis ? `${Math.round((100 * s.pouce_haut) / avis)} %` : "—"} Icon={ThumbsUp}
          teinte="rose" detail={`${s.pouce_haut} 👍 · ${s.pouce_bas} 👎`} testid="assistant-stat-avis" />
        <CarteIcone titre="Coût estimé" valeur={dollars(s.cout)} Icon={Coins} teinte="neutre"
          detail="Facturé par Anthropic" testid="assistant-stat-cout" />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Encart titre="Dernières questions" className="lg:col-span-3" testid="assistant-dernieres"
          aide="La dernière question de chaque conversation, la plus récente en haut. Cliquer une question ouvre toute la conversation."
          droite={onVoirConversations && (
            <button className="inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap" style={{ color: COULEUR_SITE }}
              onClick={onVoirConversations} data-testid="assistant-voir-conversations">
              Toutes les conversations <ArrowRight size={13} />
            </button>
          )}>
          {dernieres.length === 0 ? (
            <p className="text-sm text-[var(--muted)]">Aucune question sur la période.</p>
          ) : (
            <ul className="space-y-2">
              {dernieres.map((q, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1.5 shrink-0"><IconeCanal canal={q.canal} /></span>
                  <QuestionCliquable id={q.conversation_id} onOuvrir={onOuvrir && (() => onOuvrir(q.conversation_id))}
                    className="min-w-0 flex-1 rounded-2xl rounded-tl-md px-3.5 py-2 text-sm"
                    style={{ background: q.canal === "instagram" ? "#FDF2F8" : "#EEF2FF" }}
                    testid={`assistant-derniere-${i}`}>
                    {q.texte}
                  </QuestionCliquable>
                  <span className="shrink-0 text-[11px] text-[var(--muted)] mt-2 w-20 text-right">{ilYa(q.le)}</span>
                </li>
              ))}
            </ul>
          )}
        </Encart>
        <Encart titre="Questions sans réponse" className="lg:col-span-2" testid="assistant-sans-reponse"
          aide="L'assistant n'avait pas l'information : à ajouter sur le site ou dans ses consignes. Cliquer une question ouvre la conversation et la réponse donnée.">
          {s.sans_reponse.length === 0 ? (
            <p className="text-sm" style={{ color: "#15803D" }}>Aucune : il a trouvé la réponse à tout. 👍</p>
          ) : (
            <ul className="space-y-2">
              {s.sans_reponse.map((q, i) => (
                <li key={i}>
                  <QuestionCliquable id={q.conversation_id} onOuvrir={onOuvrir && (() => onOuvrir(q.conversation_id, "sans_reponse"))}
                    className="block w-full text-sm rounded-xl px-3 py-2 border" style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}
                    testid={`assistant-sans-reponse-${i}`}>
                    {q.question}
                    <span className="flex items-center justify-between gap-2 text-[11px] text-[var(--muted)] mt-0.5">
                      {dateCourte(q.le)}
                      {q.conversation_id && onOuvrir && (
                        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "#B45309" }}>
                          Voir la conversation <ArrowRight size={12} />
                        </span>
                      )}
                    </span>
                  </QuestionCliquable>
                </li>
              ))}
            </ul>
          )}
        </Encart>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre={s.pas === "mois" ? "Questions par mois" : "Questions par jour"} className="lg:col-span-2"
          aide={`Du ${dateCourteCH(s.du)} au ${dateCourteCH(s.au)}. Survolez une barre pour le détail.`}
          testid="assistant-graphique-cadre">
          <GraphiqueQuestions serie={s.serie || []} pas={s.pas} />
        </Encart>
        <div className="space-y-4 min-w-0">
          {montrerIg && (
            <Encart titre="Instagram : ce que l'assistant a fait" testid="assistant-stats-instagram"
              aide={`${nombre(ig.recus)} message${ig.recus > 1 ? "s" : ""} reçu${ig.recus > 1 ? "s" : ""}, même les réactions aux reels et aux stories.`}>
              <Repartition testid="assistant-stats-instagram-parts" vide="Aucun message Instagram sur la période." parts={[
                { cle: "reponses", label: "Réponses envoyées", couleur: "#16A34A", valeur: ig.reponses },
                { cle: "propositions", label: "Réponses proposées", detail: "mode observation ou version de test : rien n'est parti", couleur: "#A78BFA", valeur: ig.propositions },
                { cle: "sans", label: "Sans réponse", detail: "emojis, compliments, reels, messages pour l'équipe", couleur: "#94A3B8",
                  valeur: Math.max(ig.recus - ig.reponses - ig.propositions, 0) },
              ]} />
            </Encart>
          )}
          <Lieux lieux={s.lieux} canal={canal} />
        </div>
      </div>
    </div>
  );
}

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const jourDe = (cle) => new Date(`${cle}T12:00:00`);          // midi : jamais la veille ni le lendemain
const dateCourteCH = (cle) => (cle ? jourDe(cle).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

/* Étiquettes de l'axe : « lun. 5 » sur une semaine, « 5.10 » sur un mois ou trois, « oct. » sur
   une année ; dans l'infobulle, la date en toutes lettres. */
function etiquette(cle, pas, nbCases, long = false) {
  if (pas === "mois") {
    const [a, m] = cle.split("-").map(Number);
    return long ? `${MOIS[m - 1]} ${a}` : MOIS_COURTS[m - 1];
  }
  const d = jourDe(cle);
  if (long) return `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
  return nbCases <= 7 ? `${JOURS[d.getDay()].slice(0, 3)}. ${d.getDate()}` : `${d.getDate()} ${MOIS_COURTS[d.getMonth()]}`;
}

/* Graduations rondes : 0, 1, 2… pour quelques questions, 0, 5, 10… ou 0, 20, 40… au-delà. */
function graduations(max) {
  const pas = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000].find((p) => max / p <= 5) || Math.ceil(max / 5);
  const haut = Math.max(pas, Math.ceil(max / pas) * pas);
  return Array.from({ length: haut / pas + 1 }, (_, i) => i * pas);
}

function InfobulleQuestions({ active, payload, pas }) {
  if (!active || !payload?.length) return null;
  const c = payload[0].payload;
  // Le premier et le dernier mois d'une année ne sont pas complets : on donne leurs dates.
  const [a, m] = c.cle.split("-").map(Number);
  const partiel = pas === "mois" && (c.debut !== `${c.cle}-01` || Number(c.fin.slice(8)) !== new Date(a, m, 0).getDate());
  return (
    <div className="rounded-xl border bg-white px-3 py-2 text-xs shadow-sm" style={{ borderColor: "var(--border)" }}>
      <div className="font-semibold text-sm">{c.questions} question{c.questions > 1 ? "s" : ""}</div>
      {(c.site > 0 || c.instagram > 0) && (
        <div className="text-[var(--muted)]">site {c.site || 0} · Instagram {c.instagram || 0}</div>
      )}
      <div className="text-[var(--muted)]">{c.conversations} conversation{c.conversations > 1 ? "s" : ""}</div>
      <div className="text-[var(--muted)] mt-1">
        {etiquette(c.cle, pas, 0, true)}{partiel ? ` (du ${dateCourteCH(c.debut)} au ${dateCourteCH(c.fin)})` : ""}
      </div>
    </div>
  );
}

/* « Questions par jour » : une barre par jour de la période, les jours sans question à zéro
   (par mois sur une année). Avant, seuls les jours actifs avaient une barre : une seule
   journée remplissait tout le cadre d'un gros bloc (école, 09.10.2026). Même bibliothèque
   et mêmes couleurs que les graphiques du tableau de bord ; barres de 24 px au plus. */
function GraphiqueQuestions({ serie, pas }) {
  const total = serie.reduce((n, c) => n + c.questions, 0);
  const pic = serie.reduce((m, c) => (c.questions > (m?.questions || 0) ? c : m), null);
  const ticks = graduations(pic?.questions || 1);
  return (
    <div data-testid="assistant-graphique">
      <div style={{ height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={serie} margin={{ top: 8, right: 4, left: -18, bottom: 0 }} barCategoryGap="18%">
            <CartesianGrid vertical={false} stroke="#EEF2F6" />
            <XAxis dataKey="cle" tickFormatter={(cle) => etiquette(cle, pas, serie.length)}
              interval="preserveStartEnd" minTickGap={20} tickLine={false}
              axisLine={{ stroke: "#CBD5E1" }} tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={44}
              tick={{ fontSize: 11, fill: "#64748B" }} ticks={ticks} domain={[0, ticks[ticks.length - 1]]} />
            <Tooltip cursor={{ fill: "rgba(67,56,202,0.06)" }} content={<InfobulleQuestions pas={pas} />}
              isAnimationActive={false} />
            <Bar dataKey="site" stackId="q" fill={COULEUR_SITE} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="instagram" stackId="q" fill={COULEUR_INSTAGRAM} radius={[3, 3, 0, 0]} maxBarSize={18}
              isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[var(--muted)] mt-2">
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: COULEUR_SITE }} />Site</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: COULEUR_INSTAGRAM }} />Instagram</span>
      </div>
      <p className="text-xs text-[var(--muted)] mt-1" data-testid="assistant-graphique-resume">
        {total
          ? <>{nombre(total)} question{total > 1 ? "s" : ""} sur la période · le plus : {etiquette(pic.cle, pas, 0, true)} ({pic.questions})</>
          : "Aucune question sur la période."}
      </p>
    </div>
  );
}

/* « D'où viennent les conversations » : les pays des visiteurs du site sur la période, et la
   ville ou le canton quand on les connaît. Jamais l'adresse IP (voir chatbot_lieu.py). */
function Lieux({ lieux, canal }) {
  if (!lieux) return null;
  const max = Math.max(1, ...lieux.pays.map((p) => p.conversations));
  const connus = lieux.total - lieux.inconnu;
  const seulementFuseau = connus > 0 && Object.keys(lieux.sources || {}).every((k) => k === "fuseau");
  return (
    <Encart titre="D'où viennent les conversations" testid="assistant-lieux"
      aide="Visiteurs du site, par pays (la ville ou le canton quand on les connaît). Sans jamais garder l'adresse IP.">
      {canal === "instagram" ? (
        <p className="text-sm text-[var(--muted)]">Instagram ne dit pas d'où écrivent les gens.</p>
      ) : !lieux.pays.length ? (
        <p className="text-sm text-[var(--muted)]">Aucun lieu connu sur la période.</p>
      ) : (
        <ul className="space-y-3">
          {lieux.pays.map((p) => (
            <li key={p.pays || p.nom} data-testid="assistant-lieu">
              <div className="flex items-center gap-3 text-sm">
                <span className="w-32 shrink-0 truncate">
                  <span className="mr-1.5" aria-hidden>{drapeau(p.pays) || "🌐"}</span>{p.nom}
                </span>
                <span className="flex-1 h-2 rounded-full" style={{ background: "#EEF2F6" }}>
                  <span className="block h-2 rounded-full" style={{ width: `${(100 * p.conversations) / max}%`, background: COULEUR_SITE }} />
                </span>
                <span className="w-16 text-right tabular-nums">
                  <b>{p.conversations}</b> <span className="text-xs text-[var(--muted)]">{Math.round((100 * p.conversations) / connus)} %</span>
                </span>
              </div>
              {p.details.length > 0 && (
                <div className="text-xs text-[var(--muted)] mt-1 pl-[8.75rem]">
                  {p.details.map((d) => `${d.nom} (${d.conversations})`).join(" · ")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canal !== "instagram" && (
        <p className="text-[11px] text-[var(--muted)] mt-3">
          {lieux.inconnu > 0 && <>Lieu inconnu : {lieux.inconnu} conversation{lieux.inconnu > 1 ? "s" : ""}
            {lieux.instagram > 0 ? ` (dont ${lieux.instagram} sur Instagram, qui ne donne pas le lieu)` : ""}
            {" "}— les conversations d'avant le 09.10.2026 n'ont pas de lieu. </>}
          {seulementFuseau && "Le pays vient du fuseau horaire de l'appareil du visiteur (Europe/Zurich = Suisse) : la ville et le canton ne sont pas connus."}
        </p>
      )}
    </Encart>
  );
}

/* D'où vient la conversation : l'icône Instagram ou celle du site (comme dans FastBots). */
function IconeCanal({ canal }) {
  return canal === "instagram"
    ? <span title="Message privé Instagram" style={{ color: "#C13584" }}><Instagram size={16} /></span>
    : <span title="Assistant du site" style={{ color: "#007E85" }}><Globe size={16} /></span>;
}

const pseudo = (c) => (c.pseudo ? `@${c.pseudo}` : c.nom || "Compte Instagram");

/* « Ouverte » / « Terminée » (défini côté serveur : 30 min sans message sur le site, 24 h sur
   Instagram — le temps que Meta laisse pour répondre). */
const STATUTS_CONV = {
  ouverte: { label: "Ouverte", fond: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D",
             aide: "Le visiteur peut encore écrire (moins de 30 min sur le site, moins de 24 h sur Instagram)." },
  terminee: { label: "Terminée", fond: "#F1F5F9", bord: "#E2E8F0", texte: "#475569",
              aide: "Plus de message depuis 30 min (site) ou 24 h (Instagram)." },
};
const PastilleStatut = ({ statut }) => {
  const s = STATUTS_CONV[statut] || STATUTS_CONV.terminee;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap"
      style={{ background: s.fond, borderColor: s.bord, color: s.texte }} title={s.aide} data-testid="assistant-conv-statut">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.texte }} />{s.label}
    </span>
  );
};

/* Qui écrit : « Visiteur anonyme » et son lieu (comme « Winterthur, CH » dans FastBots), ou le
   compte Instagram. */
function Contact({ c }) {
  const ig = c.canal === "instagram";
  const lieu = c.lieu;
  const sous = ig ? (c.nom && c.pseudo ? c.nom : "Instagram")
    : lieu && lieu.pays ? `${drapeau(lieu.pays)} ${lieu.etiquette}` : "Lieu inconnu";
  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold"
        style={ig ? { background: "linear-gradient(135deg, #F58529, #DD2A7B 55%, #8134AF)", color: "#fff" }
          : { background: "#EEF6F8", color: "#007E85" }} aria-hidden>
        {ig ? (c.pseudo || c.nom || "?").charAt(0).toUpperCase() : "?"}
      </span>
      <div className="min-w-0">
        <div className="font-semibold truncate">{ig ? pseudo(c) : "Visiteur anonyme"}</div>
        <div className="text-xs text-[var(--muted)] truncate" data-testid="assistant-conv-lieu"
          title={lieu?.comment ? `Lieu estimé ${lieu.comment}` : undefined}>{sous}</div>
      </div>
    </div>
  );
}

/* Le dernier message, et ce qui mérite l'attention (sans réponse, avis, Instagram). */
function DernierMessage({ c }) {
  const m = c.dernier_message;
  return (
    <div className="min-w-0">
      <p className="line-clamp-2" title={texteSimple(m?.texte)}>
        {m?.role === "assistant" && <span className="text-[var(--muted)]">Assistant : </span>}
        {texteSimple(m?.texte) || <i className="text-[var(--muted)]">(message sans texte)</i>}
      </p>
      <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px] text-[var(--muted)]">
        <span>{c.nb_questions} question{c.nb_questions > 1 ? "s" : ""}</span>
        {c.sans_reponse > 0 && <Badge ton="ambre">sans réponse</Badge>}
        {c.pouce_haut > 0 && <span title="Avis du visiteur : utile">👍</span>}
        {c.pouce_bas > 0 && <span title="Avis du visiteur : pas utile">👎</span>}
        {c.canal === "instagram" && (
          <>
            {c.reponses > 0 && <Badge ton="vert">{c.reponses} réponse(s)</Badge>}
            {c.propositions > 0 && <Badge ton="bleu">{c.propositions} proposée(s)</Badge>}
            {c.demande_equipe
              ? <Badge ton="ambre">attend l'équipe</Badge>
              : c.reprise && <Badge ton="ambre">reprise par l'équipe</Badge>}
          </>
        )}
      </div>
    </div>
  );
}

const Debutee = ({ iso }) => (
  <div className="whitespace-nowrap">
    <div>{dateCH(iso)}</div>
    <div className="text-xs text-[var(--muted)]">{heureCH(iso)}</div>
  </div>
);

/* L'historique des conversations, à la manière de FastBots (« Chat History ») : recherche
   dans les messages, période, canal, une ligne par conversation (contact et lieu, canal,
   dernier message, début, statut, « Voir »), 30 par page. Sur téléphone : des cartes. */
function Conversations({ filtreInitial = "avec_reponse", ouvrirId = "" }) {
  /* Par défaut, les échanges où l'assistant a répondu : les messages Instagram laissés sans
     réponse (emoji, réaction à une story…) restent dans « Toutes » (école, 26.10.2026). */
  const [filtre, setFiltre] = useState(filtreInitial);
  const [canal, setCanal] = useState("");
  const [recherche, setRecherche] = useState("");
  const [du, setDu] = useState("");
  const [au, setAu] = useState("");
  const [page, setPage] = useState(0);
  const [parPage, setParPage] = useState(30);
  const [d, setD] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [ouverte, setOuverte] = useState(null);
  // Un autre filtre ramène à la première page.
  useEffect(() => { setPage(0); }, [filtre, canal, recherche, du, au, parPage]);
  useEffect(() => {
    setEnCours(true);
    const t = setTimeout(() => {
      const q = new URLSearchParams({ filtre, canal, recherche, du, au, page: String(page), par_page: String(parPage) });
      api(`/chatbot/conversations?${q}`).then(setD).catch(() => {}).finally(() => setEnCours(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filtre, canal, recherche, du, au, page, parPage]);
  const ouvrir = (c) => api(`/chatbot/conversations/${c.id}`).then(setOuverte).catch(() => {});
  useEffect(() => { if (ouvrirId) ouvrir({ id: ouvrirId }); }, [ouvrirId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reprise = async (active) => {
    await api(`/chatbot/instagram/conversations/${ouverte.id}/reprise`, { method: "POST", body: JSON.stringify({ active }) });
    ouvrir(ouverte);
  };
  const instagram = ouverte?.canal === "instagram";
  const enReprise = instagram && ouverte.reprise_jusqu_au && new Date(ouverte.reprise_jusqu_au) > new Date();
  const liste = d?.conversations || [];
  const premier = d ? d.page * d.par_page + 1 : 0;
  const lieuOuvert = !instagram && ouverte?.lieu;
  return (
    <div className="space-y-4" data-testid="assistant-conversations">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl tracking-tight">Historique des conversations</h2>
          <p className="text-xs text-[var(--muted)] mt-0.5">Les échanges de l'assistant avec les visiteurs du site et sur Instagram.</p>
        </div>
        {d && (
          <span className="ml-auto text-sm text-[var(--muted)]" data-testid="assistant-conv-total">
            {nombre(d.total)} conversation{d.total > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Recherche valeur={recherche} onChange={setRecherche} placeholder="Chercher dans les messages…"
          testid="assistant-conv-recherche" largeur={320} />
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]" data-testid="assistant-conv-periode">
          <label className="inline-flex items-center gap-1.5">Du
            <input type="date" className="input-field !w-auto !py-2 !px-3 text-sm" value={du} max={au || undefined}
              onChange={(e) => setDu(e.target.value)} data-testid="assistant-conv-du" />
          </label>
          <label className="inline-flex items-center gap-1.5">au
            <input type="date" className="input-field !w-auto !py-2 !px-3 text-sm" value={au} min={du || undefined}
              onChange={(e) => setAu(e.target.value)} data-testid="assistant-conv-au" />
          </label>
          {(du || au) && <button className="underline" onClick={() => { setDu(""); setAu(""); }}>Toutes les dates</button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Puces valeur={canal} onChange={setCanal} options={CANAUX} testid="assistant-canal" />
        <Puces valeur={filtre} onChange={setFiltre} testid="assistant-conv-filtre" options={[
          { valeur: "avec_reponse", label: "L'assistant a répondu" },
          { valeur: "", label: "Toutes" },
          { valeur: "sans_reponse", label: "Sans réponse" },
          { valeur: "avis_negatif", label: "Avis 👎" },
        ]} />
      </div>

      <div style={{ opacity: enCours && d ? 0.6 : 1, transition: "opacity .15s" }}>
        {/* Ordinateur : le tableau. */}
        <div className="hidden md:block">
          <Tableau testid="assistant-conv-tableau"
            entetes={[{ label: "Contact", largeur: 230 }, "Canal", { label: "Dernier message", large: true }, "Débutée", "Statut", { label: "Action", droite: true }]}
            lignes={liste}
            vide={d ? "Aucune conversation." : "Chargement…"}
            onClic={ouvrir}
            rendu={(c) => (
              <>
                <Cellule largeur={210} testid="assistant-conv-ligne"><Contact c={c} /></Cellule>
                <Cellule><IconeCanal canal={c.canal} /></Cellule>
                <Cellule><DernierMessage c={c} /></Cellule>
                <Cellule><Debutee iso={c.debut} /></Cellule>
                <Cellule><PastilleStatut statut={c.statut} /></Cellule>
                <Cellule droite>
                  <span className="inline-flex items-center gap-2">
                    {c.canal === "instagram" && c.pseudo && (
                      <a href={`https://www.instagram.com/${c.pseudo}/`} target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()} title="Voir le profil Instagram"
                        className="text-[var(--muted)] hover:text-[var(--text)]"><ExternalLink size={15} /></a>
                    )}
                    <button type="button" onClick={(e) => { e.stopPropagation(); ouvrir(c); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold hover:bg-white"
                      style={{ borderColor: "var(--border)", color: "#007E85" }} data-testid="assistant-conv-voir">
                      <Eye size={14} /> Voir
                    </button>
                  </span>
                </Cellule>
              </>
            )}
          />
        </div>
        {/* Téléphone : une carte par conversation, rien ne déborde. */}
        <div className="md:hidden space-y-2" data-testid="assistant-conv-cartes">
          {!liste.length && <p className="text-sm text-[var(--muted)] px-1 py-4">{d ? "Aucune conversation." : "Chargement…"}</p>}
          {liste.map((c) => (
            <button key={c.id} type="button" onClick={() => ouvrir(c)} data-testid="assistant-conv-carte"
              className="w-full text-left bg-white rounded-2xl border p-4 space-y-2" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1"><Contact c={c} /></div>
                <PastilleStatut statut={c.statut} />
              </div>
              <div className="text-sm"><DernierMessage c={c} /></div>
              <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                <IconeCanal canal={c.canal} />
                <span>{dateCH(c.debut)} · {heureCH(c.debut)}</span>
                <span className="ml-auto inline-flex items-center gap-1 font-semibold" style={{ color: "#007E85" }}><Eye size={14} /> Voir</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {d && d.total > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--muted)]" data-testid="assistant-conv-pagination">
          <label className="inline-flex items-center gap-2">
            <select className="border rounded-lg px-2 py-1 bg-white text-[var(--text)]" style={{ borderColor: "var(--border)" }}
              value={parPage} onChange={(e) => setParPage(Number(e.target.value))} data-testid="assistant-conv-par-page">
              {[30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            par page
          </label>
          <span>Affichage de {premier} à {premier + liste.length - 1} sur {nombre(d.total)} conversation{d.total > 1 ? "s" : ""}</span>
          <span className="ml-auto inline-flex items-center gap-2">
            Page {d.page + 1} sur {d.pages}
            <button type="button" className="w-8 h-8 rounded-full border flex items-center justify-center bg-white disabled:opacity-40"
              style={{ borderColor: "var(--border)" }} disabled={d.page <= 0} onClick={() => setPage(d.page - 1)}
              aria-label="Page précédente" data-testid="assistant-conv-precedente"><ChevronLeft size={15} /></button>
            <button type="button" className="w-8 h-8 rounded-full border flex items-center justify-center bg-white disabled:opacity-40"
              style={{ borderColor: "var(--border)" }} disabled={d.page >= d.pages - 1} onClick={() => setPage(d.page + 1)}
              aria-label="Page suivante" data-testid="assistant-conv-suivante"><ChevronRight size={15} /></button>
          </span>
        </div>
      )}

      {ouverte && (
        <Modale
          titre={instagram ? `Instagram · ${pseudo(ouverte)}` : "Conversation sur le site"}
          sous_titre={instagram
            ? `${ouverte.nom && ouverte.pseudo ? `${ouverte.nom} · ` : ""}depuis le ${heure(ouverte.debut)}`
            : `${heure(ouverte.debut)}${ouverte.page ? ` · depuis ${ouverte.page}` : ""}`}
          onFermer={() => setOuverte(null)} large testid="assistant-conversation">
          {lieuOuvert && (
            <p className="text-xs text-[var(--muted)] -mt-3 mb-4" data-testid="assistant-conversation-lieu">
              {drapeau(ouverte.lieu.pays)} {lieuTexte(ouverte.lieu)}
            </p>
          )}
          {instagram && (
            <div className="flex flex-wrap items-center gap-3 text-xs mb-4 p-3 rounded-xl" style={{ background: "#F8FAFC" }}>
              {enReprise ? (
                <>
                  <span>{ouverte.reprise_par === "la personne a demandé l'équipe"
                    ? `La personne a demandé quelqu'un de l'équipe : l'assistant le lui a dit et se tait jusqu'au ${heure(ouverte.reprise_jusqu_au)}. Répondez-lui depuis Instagram.`
                    : `L'équipe a repris la conversation : l'assistant se tait jusqu'au ${heure(ouverte.reprise_jusqu_au)}.`}</span>
                  <button className="underline" onClick={() => reprise(false)} data-testid="assistant-ig-rendre">Rendre la main à l'assistant</button>
                </>
              ) : (
                <>
                  <span>L'assistant peut répondre dans cette conversation.</span>
                  <button className="underline" onClick={() => reprise(true)} data-testid="assistant-ig-reprendre">L'équipe reprend la conversation</button>
                </>
              )}
              {ouverte.pseudo && (
                <a className="underline ml-auto" href={`https://www.instagram.com/${ouverte.pseudo}/`} target="_blank" rel="noopener noreferrer">
                  Voir le profil
                </a>
              )}
            </div>
          )}
          <Fil messages={ouverte.messages} avis={ouverte.avis || {}} />
          {(ouverte.sans_reponse || []).length > 0 && (
            <p className="text-xs mt-4" style={{ color: "#B45309" }}>
              Signalé sans réponse : {ouverte.sans_reponse.join(" · ")}
            </p>
          )}
        </Modale>
      )}
    </div>
  );
}

/* Le lieu d'une conversation en clair, et comment on le sait. */
function lieuTexte(l) {
  const ou = [l.ville, l.region, l.nom_pays].filter(Boolean).join(", ") || "Lieu inconnu";
  return [ou, l.comment ? `estimé ${l.comment}` : "", l.langue ? `langue du navigateur : ${l.langue}` : ""]
    .filter(Boolean).join(" · ");
}

/* Ce que l'assistant a fait d'un message Instagram reçu, en une ligne. */
function decisionInstagram(m) {
  const d = m.decision;
  if (!d) return null;
  if (!d.repondre) return { texte: `Pas de réponse : ${d.raison}`, couleur: "var(--muted)" };
  if (m.erreur) return { texte: `Pas de réponse : ${m.erreur}`, couleur: "#B91C1C" };
  if (m.repondu) return { texte: "Réponse envoyée", couleur: "#15803D" };
  return null;   // la bulle de l'assistant, juste dessous, dit ce qui s'est passé
}

const STATUTS_REPONSE = {
  envoyee: { texte: "Envoyée sur Instagram", couleur: "#15803D" },
  partielle: { texte: "Envoyée en partie", couleur: "#B45309" },
  observation: { texte: "Mode observation : l'assistant aurait répondu ceci (rien n'a été envoyé)", couleur: "#1D4ED8" },
  test: { texte: "Version de test : réponse non envoyée à Instagram", couleur: "#1D4ED8" },
  erreur: { texte: "Pas envoyée", couleur: "#B91C1C" },
};

/* Le fil d'une conversation. Instagram ajoute trois choses : la décision de l'assistant sous
   chaque message reçu (« Pas de réponse : emoji seul »), l'état de chaque réponse (envoyée,
   proposée en mode observation…) et les messages écrits par l'équipe depuis Instagram. */
function Fil({ messages, avis = {} }) {
  return (
    <div className="space-y-3">
      {messages.map((m, i) => {
        const recu = m.role === "user";
        const equipe = m.role === "equipe";
        const decision = recu ? decisionInstagram(m) : null;
        const statut = m.role === "assistant" && m.statut ? STATUTS_REPONSE[m.statut] : null;
        const fond = recu ? { background: "var(--primary)", color: "#04202A" }
          : equipe ? { background: "#FDF2F8", border: "1px solid #FBCFE8" }
            : m.statut && m.statut !== "envoyee" ? { background: "#F1F5F9", border: "1px dashed #94A3B8" }
              : { background: "#F1F5F9" };
        return (
          <div key={i} className={`flex flex-col ${recu ? "items-end" : "items-start"}`}>
            {equipe && <span className="text-[11px] text-[var(--muted)] mb-0.5">L'équipe, depuis Instagram</span>}
            {recu && m.story && (
              <span className="text-[11px] text-[var(--muted)] mb-0.5 inline-flex items-center gap-1">
                <CornerDownRight size={11} /> réponse à une story
              </span>
            )}
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm${recu || equipe ? " whitespace-pre-wrap" : ""}`} style={fond}>
              {recu || equipe
                ? (m.texte || <i>({m.decision?.raison || "message sans texte"})</i>)
                : <TexteAssistant texte={m.texte} nouvelOnglet />}
              {m.role === "assistant" && (avis[i] || (m.outils || []).length > 0) && (
                <div className="text-[11px] text-[var(--muted)] mt-1.5 flex gap-3">
                  {avis[i] === 1 && <span><ThumbsUp size={12} className="inline" /> utile</span>}
                  {avis[i] === -1 && <span><ThumbsDown size={12} className="inline" /> pas utile</span>}
                  {(m.outils || []).length > 0 && <span>a lu : {m.outils.map((o) => o.replace("page:", "")).join(", ")}</span>}
                </div>
              )}
            </div>
            {decision && (
              <span className="text-[11px] mt-0.5 inline-flex items-center gap-1" style={{ color: decision.couleur }} data-testid="assistant-ig-decision">
                {m.repondu ? <Check size={11} /> : <X size={11} />} {decision.texte}
              </span>
            )}
            {statut && (
              <span className="text-[11px] mt-0.5" style={{ color: statut.couleur }}>
                {statut.texte}{m.morceaux > 1 ? ` · en ${m.morceaux} messages` : ""}{m.erreur ? ` · ${m.erreur}` : ""}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Consignes({ ecran, onEnregistre, ecranIg, onEnregistreIg, initial = "site" }) {
  const [onglet, setOnglet] = useState(initial);
  return (
    <div className="space-y-6">
      <Puces valeur={onglet} onChange={setOnglet} testid="assistant-reglages-canal" options={[
        { valeur: "site", label: "Site web" },
        { valeur: "instagram", label: "Instagram" },
        { valeur: "savoir", label: "Ce qu'il sait" },
      ]} />
      {onglet === "site" && <ReglagesSite ecran={ecran} onEnregistre={onEnregistre} />}
      {onglet === "instagram" && (ecranIg
        ? <ReglagesInstagram ecran={ecranIg} onEnregistre={onEnregistreIg} />
        : <p className="text-sm text-[var(--muted)]">Chargement…</p>)}
      {onglet === "savoir" && <Savoir />}
    </div>
  );
}

/* Tester : comme sur le site, comme sur Instagram, ou le test complet de 50 questions. */
function Tester({ ecran }) {
  const [onglet, setOnglet] = useState("site");
  return (
    <div className="space-y-6">
      <Puces valeur={onglet} onChange={setOnglet} testid="assistant-tester-canal" options={[
        { valeur: "site", label: "Comme sur le site" },
        { valeur: "instagram", label: "Comme sur Instagram" },
        { valeur: "complet", label: "Test complet (50 questions)" },
      ]} />
      {onglet === "site" && <Essai ecran={ecran} />}
      {onglet === "instagram" && <EssaiInstagram />}
      {onglet === "complet" && <Evaluation />}
    </div>
  );
}

function ReglagesSite({ ecran, onEnregistre }) {
  const [f, setF] = useState(ecran.reglages);
  const [msg, setMsg] = useState("");
  const [enCours, setEnCours] = useState(false);
  const maj = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const enregistrer = async () => {
    setEnCours(true); setMsg("");
    try {
      const r = await api("/chatbot", { method: "PUT", body: JSON.stringify({ ...f, plafond_jour: Number(f.plafond_jour) || 0,
        suggestions: (f.suggestions || []).map((x) => x.trim()).filter(Boolean) }) });
      onEnregistre(r); setF(r.reglages); setMsg("Enregistré.");
    } catch (e) { setMsg(e.message); } finally { setEnCours(false); }
  };
  /* En ligne / en pause : enregistré tout de suite, sans emporter ce qui est en cours de
     saisie plus bas (on repart des réglages enregistrés). */
  const enLigne = f.fournisseur === "maison";
  const changerMode = async (v) => {
    if ((v === "maison") === enLigne) return;
    if (v !== "maison" && !window.confirm("Mettre l'assistant du site en pause ? La bulle disparaît du site jusqu'à ce que tu le remettes en ligne.")) return;
    setEnCours(true); setMsg("");
    try {
      const r = await api("/chatbot", { method: "PUT", body: JSON.stringify({ ...ecran.reglages, fournisseur: v }) });
      onEnregistre(r); setF((x) => ({ ...x, fournisseur: r.reglages.fournisseur }));
      setMsg(v === "maison" ? "L'assistant est en ligne sur le site." : "L'assistant est en pause sur le site.");
    } catch (e) { setMsg(e.message); } finally { setEnCours(false); }
  };
  return (
    <div className="space-y-6" data-testid="assistant-reglages">
      <Section titre="Sur le site">
        <Puces valeur={enLigne ? "maison" : "aucun"} onChange={changerMode} testid="assistant-fournisseur" options={MODES_SITE} />
        <p className="text-sm">{enLigne
          ? "La bulle de l'assistant est sur toutes les pages du site."
          : "En pause : la bulle n'apparaît plus sur le site."}</p>
      </Section>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Message d'accueil</span>
        <input className="input-field mt-1" value={f.accueil} maxLength={400} onChange={(e) => maj("accueil")(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Questions proposées à l'ouverture (une par ligne, 6 au plus)</span>
        <textarea className="input-field mt-1 text-sm" rows={5} value={(f.suggestions || []).join("\n")}
          onChange={(e) => maj("suggestions")(e.target.value.split("\n"))} data-testid="assistant-suggestions-champ" />
        <span className="text-xs text-[var(--muted)]">Le visiteur clique dessus au lieu de taper sa question.</span>
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Consignes (prompt)</span>
        <textarea className="input-field mt-1 font-mono text-xs" rows={16} value={f.prompt}
          onChange={(e) => maj("prompt")(e.target.value)} data-testid="assistant-prompt" />
        <span className="text-xs text-[var(--muted)]">
          Le ton, la façon de conseiller, ce qu'il faut toujours proposer… Les informations (horaires, prix, dates)
          ne s'écrivent pas ici : elles viennent du site et des Réglages, toujours à jour.
        </span>
      </label>
      <details className="rounded-2xl border px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid="assistant-reglages-avances">
        <summary className="cursor-pointer text-sm font-semibold">Réglages avancés</summary>
        <p className="text-xs text-[var(--muted)] mt-1">Rarement utiles : le modèle, la réflexion et le plafond par jour.</p>
        <div className="grid md:grid-cols-2 gap-x-6 mt-4">
          <ChampChoix label="Modèle" valeur={f.modele} onChange={maj("modele")} testid="assistant-modele"
            options={ecran.modeles.map((m) => ({ valeur: m.id, label: m.nom }))}
            aide="Testez dans « Tester » avant de changer." />
          <ChampChoix label="Réflexion" valeur={f.effort} options={EFFORTS} onChange={maj("effort")} />
          <label className="block mb-4">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Plafond de questions par jour</span>
            <input className="input-field mt-1" type="number" min="0" max="5000" value={f.plafond_jour}
              onChange={(e) => maj("plafond_jour")(e.target.value)} />
            <span className="text-xs text-[var(--muted)]">Site et Instagram ensemble. Protège le budget si un robot pose des questions en boucle.</span>
          </label>
        </div>
      </details>
      <div className="flex flex-wrap items-center gap-3">
        <BoutonSombre onClick={enregistrer} disabled={enCours} testid="assistant-enregistrer">Enregistrer</BoutonSombre>
        <button className="text-xs underline text-[var(--muted)]" onClick={() => maj("prompt")(ecran.prompt_defaut)}>
          <RotateCcw size={12} className="inline mr-1" />Consignes d'origine
        </button>
        {msg && <span className="text-sm">{msg}</span>}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-[var(--muted)]">Règles fixes (non modifiables : confidentialité, pas d'invention)</summary>
        <pre className="whitespace-pre-wrap text-xs mt-2 p-3 rounded-xl" style={{ background: "#F1F5F9" }}>{ecran.garde_fous}</pre>
      </details>
      {ecran.versions.length > 0 && (
        <Section titre="Versions précédentes des consignes" aide="Cliquer « Reprendre » remet le texte dans l'éditeur ; il faut ensuite enregistrer.">
          <ul className="space-y-2 text-sm">
            {ecran.versions.map((v, i) => (
              <li key={i} className="flex flex-wrap gap-3 items-center">
                <span className="text-[var(--muted)]">Remplacée le {heure(v.remplace_le)}{v.par ? ` par ${v.par}` : ""}</span>
                <span className="truncate max-w-[50ch]">{v.prompt.slice(0, 90)}…</span>
                <button className="text-xs underline" onClick={() => maj("prompt")(v.prompt)}>Reprendre</button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

const ETIQUETTE = "text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]";
const VARIABLES_IG = {
  META_APP_SECRET: "Code secret de l'application Meta",
  META_VERIFY_TOKEN: "Jeton de vérification (inventé par vous)",
  INSTAGRAM_ACCESS_TOKEN: "Jeton d'accès Instagram",
  INSTAGRAM_ACCOUNT_ID: "Identifiant du compte Instagram (facultatif)",
};

/* Ce qui ne va pas dans le branchement Instagram, en phrases. Vide : tout va bien. */
function problemesInstagram(st) {
  const p = [];
  const variables = st.variables || {};
  const manquantes = Object.keys(VARIABLES_IG).filter((k) => k !== "INSTAGRAM_ACCOUNT_ID" && !variables[k]);
  if (manquantes.length) p.push(`Absent dans Render : ${manquantes.join(", ")}.`);
  if (st.cle_anthropic === false) p.push("La clé Anthropic (ANTHROPIC_API_KEY) manque dans Render.");
  if (st.jeton_expire_le) {
    const jours = Math.round((new Date(st.jeton_expire_le) - Date.now()) / 86400000);
    if (jours <= 10) {
      p.push(`Le jeton Instagram expire le ${String(st.jeton_expire_le).slice(0, 10).split("-").reverse().join(".")} : `
        + "dans Meta, générer un nouveau jeton et le coller dans Render → INSTAGRAM_ACCESS_TOKEN.");
    }
  }
  if (st.jeton_erreur) p.push(`Prolongation du jeton refusée par Meta : ${st.jeton_erreur}`);
  if (st.derniere_erreur && (!st.dernier_envoi || st.derniere_erreur.le > st.dernier_envoi)) {
    p.push(`Dernier envoi refusé (${heure(st.derniere_erreur.le)}) : ${st.derniere_erreur.message}`);
  }
  if (st.signature_refusee && (!st.dernier_evenement || st.signature_refusee > st.dernier_evenement)) {
    p.push(`Appel refusé le ${heure(st.signature_refusee)} : signature fausse. Dans Render, mettre dans META_APP_SECRET `
      + "l'autre clé secrète : « Clé secrète de l'app Instagram » (Configuration de l'API avec connexion Instagram) "
      + "ou « Clé secrète » de l'app Meta (Paramètres de l'app → Général).");
  }
  return p;
}

/* Instagram (simplifié le 12.10.2026, accord de l'école) : l'état du branchement en une
   ligne (un bandeau rouge seulement s'il y a un problème, les détails techniques repliés),
   « Répond » / « En pause », puis ce que l'assistant dit. Le pas-à-pas du branchement est
   dans le manuel (« Instagram : rebrancher en cas de panne »). Aucune valeur secrète
   n'arrive jusqu'ici : seulement « présente » ou « absente ». */
function ReglagesInstagram({ ecran, onEnregistre }) {
  const [f, setF] = useState(ecran.reglages);
  const [msg, setMsg] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [copie, setCopie] = useState(false);
  const maj = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const st = ecran.statut || {};
  const corps = (r) => ({
    ...r,
    instagram_reprise_heures: Number(r.instagram_reprise_heures) || 24,
    instagram_relance_presentation_heures: Number(r.instagram_relance_presentation_heures) || 12,
  });
  const enregistrer = async () => {
    setEnCours(true); setMsg("");
    try {
      const r = await api("/chatbot/instagram", { method: "PUT", body: JSON.stringify(corps(f)) });
      onEnregistre(r); setF(r.reglages); setMsg("Enregistré.");
    } catch (e) { setMsg(e.message); } finally { setEnCours(false); }
  };
  /* Répond / En pause : enregistré tout de suite, sans emporter ce qui est en cours de saisie. */
  const changerMode = async (mode) => {
    if (mode === f.instagram_mode) return;
    if (mode === "eteint" && !window.confirm("Mettre l'assistant en pause sur Instagram ? Les messages arriveront toujours ici, mais il ne répondra plus : l'équipe répond depuis Instagram.")) return;
    setEnCours(true); setMsg("");
    try {
      const r = await api("/chatbot/instagram", { method: "PUT", body: JSON.stringify(corps({ ...ecran.reglages, instagram_mode: mode })) });
      onEnregistre(r); setF((x) => ({ ...x, instagram_mode: r.reglages.instagram_mode }));
      setMsg(mode === "actif" ? "L'assistant répond sur Instagram." : "L'assistant est en pause sur Instagram.");
    } catch (e) { setMsg(e.message); } finally { setEnCours(false); }
  };
  const copier = () => {
    navigator.clipboard?.writeText(st.url_webhook).then(() => { setCopie(true); setTimeout(() => setCopie(false), 1500); });
  };
  const mode = MODES_IG[f.instagram_mode] || MODES_IG.eteint;
  const modes = ["actif", "eteint", ...(f.instagram_mode === "observation" ? ["observation"] : [])];
  const problemes = problemesInstagram(st);
  const jeton = st.jeton_expire_le ? String(st.jeton_expire_le).slice(0, 10).split("-").reverse().join(".") : "";
  return (
    <div className="space-y-8" data-testid="assistant-instagram">
      {problemes.length === 0 ? (
        <div className="rounded-2xl border px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1"
          style={{ background: "#F0FDF4", borderColor: "#BBF7D0", color: "#14532D" }} data-testid="assistant-ig-statut">
          <span className="inline-flex items-center gap-1.5 font-semibold"><Check size={15} /> Instagram branché</span>
          <span>· dernier message reçu {st.dernier_evenement ? heure(st.dernier_evenement) : "—"}</span>
          {st.dernier_envoi && <span>· dernière réponse {heure(st.dernier_envoi)}</span>}
          {jeton && <span data-testid="ig-jeton-echeance">· jeton valable jusqu'au {jeton} (prolongé tout seul)</span>}
        </div>
      ) : (
        <div className="rounded-2xl border px-4 py-3 text-sm" style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#7F1D1D" }}
          data-testid="assistant-ig-statut">
          <div className="font-semibold inline-flex items-center gap-1.5"><X size={15} /> Instagram : à vérifier</div>
          <ul className="list-disc pl-5 mt-1 space-y-1">{problemes.map((t) => <li key={t}>{t}</li>)}</ul>
          <p className="text-xs mt-2">Le pas-à-pas complet est dans le manuel : « Instagram : rebrancher en cas de panne ».</p>
        </div>
      )}

      <Section titre="Sur Instagram"
        aide="Il ne répond qu'aux questions claires sur l'école ; jamais aux emojis, compliments, réponses de story ni messages d'amis. Il dit toujours qu'il est l'assistant virtuel, et se tait quand quelqu'un de l'équipe répond depuis Instagram.">
        <Puces valeur={f.instagram_mode} onChange={changerMode} testid="assistant-ig-mode"
          options={modes.map((valeur) => ({ valeur, label: MODES_IG[valeur].label }))} />
        <p className="text-sm">{mode.aide}</p>
        {st.version_de_test && (
          <p className="text-xs" style={{ color: "#1D4ED8" }}>
            Version de test : rien n'est jamais envoyé à Instagram, même en mode « Répond » (la réponse est seulement gardée ici).
          </p>
        )}
      </Section>

      <Section titre="Se présenter comme une IA" aide="Ajouté automatiquement : sur Instagram, les gens croient écrire à une personne de l'équipe.">
        <div className="grid md:grid-cols-2 gap-x-6">
          <label className="block mb-4">
            <span className={ETIQUETTE}>Présentation (première réponse)</span>
            <input className="input-field mt-1" value={f.instagram_presentation} maxLength={200}
              onChange={(e) => maj("instagram_presentation")(e.target.value)} data-testid="assistant-ig-presentation" />
            <span className="text-xs text-[var(--muted)]">En tête de la première réponse, et de nouveau après un long silence.</span>
          </label>
          <label className="block mb-4">
            <span className={ETIQUETTE}>Signature (réponses suivantes)</span>
            <input className="input-field mt-1" value={f.instagram_signature} maxLength={100}
              onChange={(e) => maj("instagram_signature")(e.target.value)} data-testid="assistant-ig-signature" />
            <span className="text-xs text-[var(--muted)]">Les deux doivent dire « assistant virtuel » (ou 🤖).</span>
          </label>
        </div>
      </Section>

      <Section titre="Passer la main à l'équipe" aide="Quand la personne ne veut pas parler à un robot, est mécontente, insulte ou que l'échange tourne mal.">
        <label className="block">
          <span className={ETIQUETTE}>Réponse, une seule fois</span>
          <textarea className="input-field mt-1" rows={3} maxLength={500} value={f.instagram_passage_main || ""}
            onChange={(e) => maj("instagram_passage_main")(e.target.value)} data-testid="assistant-ig-passage-main" />
          <span className="text-xs text-[var(--muted)]">
            Envoyée telle quelle (la présentation ou la signature « assistant virtuel » s'ajoute toute seule). Ensuite
            l'assistant se tait 30 jours dans cette conversation, et « À traiter » prévient l'équipe jusqu'à ce que
            quelqu'un réponde depuis Instagram. « Rendre la main à l'assistant », dans la conversation, le fait reparler.
          </span>
        </label>
        <button className="text-xs underline text-[var(--muted)]" onClick={() => maj("instagram_passage_main")(ecran.defauts.instagram_passage_main)}>
          <RotateCcw size={12} className="inline mr-1" />Texte d'origine
        </button>
      </Section>

      <Section titre="Consignes pour Instagram" aide="Ajoutées à celles du site (mêmes informations, même ton) : réponses courtes, texte simple, à quoi répondre.">
        <textarea className="input-field font-mono text-xs" rows={14} value={f.instagram_consignes}
          onChange={(e) => maj("instagram_consignes")(e.target.value)} data-testid="assistant-ig-consignes" />
        <button className="text-xs underline text-[var(--muted)]" onClick={() => maj("instagram_consignes")(ecran.consignes_defaut)}>
          <RotateCcw size={12} className="inline mr-1" />Consignes d'origine
        </button>
        {(ecran.versions || []).length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-[var(--muted)]">Versions précédentes ({ecran.versions.length})</summary>
            <ul className="space-y-2 mt-2">
              {ecran.versions.map((v, i) => (
                <li key={i} className="flex flex-wrap gap-3 items-center">
                  <span className="text-[var(--muted)]">Remplacée le {heure(v.remplace_le)}{v.par ? ` par ${v.par}` : ""}</span>
                  <span className="truncate max-w-[50ch]">{v.prompt.slice(0, 90)}…</span>
                  <button className="text-xs underline" onClick={() => maj("instagram_consignes")(v.prompt)}>Reprendre</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Section>

      <details className="rounded-2xl border px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid="assistant-ig-avances">
        <summary className="cursor-pointer text-sm font-semibold">Réglages avancés</summary>
        <div className="grid md:grid-cols-2 gap-x-6 mt-4">
          <label className="block mb-4">
            <span className={ETIQUETTE}>Se présenter de nouveau après (heures)</span>
            <input className="input-field mt-1" type="number" min="1" max="168" value={f.instagram_relance_presentation_heures}
              onChange={(e) => maj("instagram_relance_presentation_heures")(e.target.value)} />
          </label>
          <label className="block mb-4">
            <span className={ETIQUETTE}>Se taire quand l'équipe répond (heures)</span>
            <input className="input-field mt-1" type="number" min="1" max="168" value={f.instagram_reprise_heures}
              onChange={(e) => maj("instagram_reprise_heures")(e.target.value)} />
            <span className="text-xs text-[var(--muted)]">Quand quelqu'un répond lui-même depuis Instagram, l'assistant ne dit plus rien dans cette conversation pendant ce temps.</span>
          </label>
        </div>
      </details>

      <div className="flex flex-wrap items-center gap-3">
        <BoutonSombre onClick={enregistrer} disabled={enCours} testid="assistant-ig-enregistrer">Enregistrer</BoutonSombre>
        {msg && <span className="text-sm">{msg}</span>}
      </div>

      <details className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: "var(--border)" }}
        open={problemes.length > 0} data-testid="assistant-ig-details">
        <summary className="cursor-pointer font-semibold">Détails techniques (en cas de panne)</summary>
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div className="space-y-2">
            <div className={ETIQUETTE}>Variables dans Render</div>
            <ul className="space-y-1">
              {Object.entries(VARIABLES_IG).map(([cle, libelle]) => (
                <li key={cle} className="flex items-center gap-2">
                  {(st.variables || {})[cle] ? <Check size={14} style={{ color: "#15803D" }} /> : <X size={14} style={{ color: "#B91C1C" }} />}
                  <span>{libelle}</span> <code className="text-[11px] text-[var(--muted)]">{cle}</code>
                </li>
              ))}
              <li className="flex items-center gap-2">
                {st.cle_anthropic ? <Check size={14} style={{ color: "#15803D" }} /> : <X size={14} style={{ color: "#B91C1C" }} />}
                <span>Clé Anthropic</span> <code className="text-[11px] text-[var(--muted)]">ANTHROPIC_API_KEY</code>
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className={ETIQUETTE}>Ce que Meta a fait</div>
            <ul className="space-y-1">
              <li>Adresse vérifiée par Meta : {st.verification ? heure(st.verification) : "pas encore"}</li>
              <li>Dernier message reçu : {st.dernier_evenement ? heure(st.dernier_evenement) : "aucun"}</li>
              <li>Dernière réponse envoyée : {st.dernier_envoi ? heure(st.dernier_envoi) : "aucune"}</li>
              {jeton && <li>Jeton valable jusqu'au {jeton} <span className="text-[var(--muted)]">(prolongé tout seul chaque semaine)</span></li>}
            </ul>
          </div>
        </div>
        {st.url_webhook && (
          <div className="mt-4">
            <div className={ETIQUETTE}>Adresse donnée à Meta (« URL de rappel »)</div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <code className="px-3 py-1.5 rounded-lg text-xs" style={{ background: "#F1F5F9", overflowWrap: "anywhere" }}>{st.url_webhook}</code>
              <button className="text-xs underline inline-flex items-center gap-1" onClick={copier}>
                {copie ? <Check size={12} /> : <Copy size={12} />} {copie ? "Copiée" : "Copier"}
              </button>
            </div>
          </div>
        )}
      </details>
    </div>
  );
}

/* Essayer un message comme s'il arrivait sur Instagram : le tri (répondre ou non, et
   pourquoi), puis la réponse exacte, présentation comprise. Rien n'est envoyé ni gardé. */
function EssaiInstagram() {
  const [question, setQuestion] = useState("");
  const [story, setStory] = useState(false);
  const [fil, setFil] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [info, setInfo] = useState("");
  const essayer = async () => {
    const q = question.trim();
    if (!q || enCours) return;
    setEnCours(true); setInfo("");
    try {
      const historique = fil.flatMap((x) => [{ role: "user", texte: x.question },
        ...(x.r.texte ? [{ role: "assistant", texte: x.r.texte }] : [])]);
      const r = await api("/chatbot/instagram/essai", { method: "POST", body: JSON.stringify({ message: q, story, historique }) });
      setFil([...fil, { question: q, story, r }]); setQuestion("");
      setInfo(`coût ${(r.cout || 0).toFixed(4)} $`);
    } catch (e) { setInfo(e.message); } finally { setEnCours(false); }
  };
  return (
    <Section titre="Essayer un message Instagram" aide="Ce que l'assistant ferait de ce message. Rien n'est envoyé ni gardé.">
      <div className="space-y-3 max-w-3xl" data-testid="assistant-ig-essai">
        {fil.map((x, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex flex-col items-end">
              {x.story && <span className="text-[11px] text-[var(--muted)]">réponse à une story</span>}
              <div className="max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap" style={{ background: "var(--primary)", color: "#04202A" }}>{x.question}</div>
              <span className="text-[11px] mt-0.5" style={{ color: x.r.decision.repondre ? "#15803D" : "var(--muted)" }}>
                {x.r.passer_la_main
                  ? `Passe la main à l'équipe (${x.r.decision.raison}) : cette réponse, puis plus rien dans la conversation`
                  : x.r.decision.repondre ? "Répondrait" : `Pas de réponse : ${x.r.decision.raison}`}
              </span>
            </div>
            {x.r.morceaux.map((t, k) => (
              <div key={k} className="max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap" style={{ background: "#F1F5F9" }}>{t}</div>
            ))}
          </div>
        ))}
        <div className="flex flex-wrap gap-2 items-center">
          <input className="input-field flex-1 min-w-[240px]" value={question} placeholder="Ex. : trop beau 😍 · ou : c'est quand le stage de Pâques ?"
            onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && essayer()} data-testid="assistant-ig-essai-question" />
          <BoutonSombre onClick={essayer} disabled={enCours}><Send size={14} /></BoutonSombre>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--muted)]">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={story} onChange={(e) => setStory(e.target.checked)} /> Réponse à une story
          </label>
          {enCours && <span>L'assistant réfléchit…</span>}
          <span>{info}</span>
          {fil.length > 0 && <button className="underline ml-auto" onClick={() => { setFil([]); setInfo(""); }}>Nouvelle conversation</button>}
        </div>
      </div>
    </Section>
  );
}

/* Le bac à sable : une conversation avec l'assistant tel qu'il est enregistré (consignes
   comprises — elles se changent dans « Réglages & consignes »). Le modèle peut être essayé
   ici sans rien changer au site. Rien n'est gardé dans les statistiques. */
function Essai({ ecran }) {
  const [modele, setModele] = useState(ecran.reglages.modele);
  const [fil, setFil] = useState([]);
  const [question, setQuestion] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [info, setInfo] = useState("");
  const fond = useRef(null);
  useEffect(() => { fond.current?.scrollIntoView({ block: "nearest" }); }, [fil, enCours]);
  const envoyer = async (texte) => {
    const q = (typeof texte === "string" ? texte : question).trim();
    if (!q || enCours) return;
    setEnCours(true); setInfo(""); setQuestion("");
    const historique = fil;
    setFil([...historique, { role: "user", texte: q }]);
    try {
      const r = await api("/chatbot/essai", { method: "POST", body: JSON.stringify({ message: q, historique, modele }) });
      setFil([...historique, { role: "user", texte: q }, { role: "assistant", texte: r.texte, outils: r.outils }]);
      setInfo(`coût ${r.cout.toFixed(4)} $`);
    } catch (e) {
      setFil(historique); setInfo(e.message);
    } finally { setEnCours(false); }
  };
  return (
    <div className="max-w-3xl space-y-3" data-testid="assistant-essai">
      <p className="text-sm text-[var(--muted)]">
        Posez vos questions comme un visiteur du site. L'assistant répond avec les consignes enregistrées ;
        les essais ne comptent pas dans les statistiques.
      </p>
      <div className="rounded-2xl border p-4 min-h-[380px] max-h-[60vh] overflow-y-auto bg-white" style={{ borderColor: "var(--border)" }}>
        {fil.length ? <Fil messages={fil} /> : (
          <div className="space-y-3">
            <p className="text-sm">{ecran.reglages.accueil}</p>
            <div className="flex flex-wrap gap-2">
              {(ecran.reglages.suggestions || []).map((sug) => (
                <button key={sug} onClick={() => envoyer(sug)} className="text-xs px-3 py-1.5 rounded-full border"
                  style={{ borderColor: "var(--primary)", color: "#007E85" }}>{sug}</button>
              ))}
            </div>
          </div>
        )}
        {enCours && <p className="text-sm text-[var(--muted)] mt-3">L'assistant réfléchit…</p>}
        <div ref={fond} />
      </div>
      <div className="flex gap-2">
        <input className="input-field flex-1" value={question} placeholder="Ex. : quels cours le vendredi pour une fille de 9 ans ?"
          onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && envoyer()} data-testid="assistant-essai-question" />
        <BoutonSombre onClick={() => envoyer()} disabled={enCours}><Send size={14} /></BoutonSombre>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--muted)]">
        <label className="flex items-center gap-2">
          Modèle
          <select className="border rounded-lg px-2 py-1 bg-white" style={{ borderColor: "var(--border)" }}
            value={modele} onChange={(e) => setModele(e.target.value)} data-testid="assistant-essai-modele">
            {ecran.modeles.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
          </select>
        </label>
        <span>{info}</span>
        {fil.length > 0 && <button className="underline ml-auto" onClick={() => { setFil([]); setInfo(""); }}>Nouvelle conversation</button>}
      </div>
    </div>
  );
}

const VERDICTS = {
  juste: { label: "Juste", ton: "vert" },
  partiel: { label: "Partiel", ton: "ambre" },
  faux: { label: "Faux", ton: "rouge" },
  erreur: { label: "Erreur", ton: "rouge" },
};

/* L'évaluation : une cinquantaine de questions de familles posées d'un coup. Le planning est
   vérifié par le calcul (aucun cours oublié ni en trop) ; le reste est relu par un modèle
   plus fort qui a tout le site sous les yeux. */
function Evaluation() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [filtre, setFiltre] = useState("");
  const [ouverte, setOuverte] = useState(null);
  const charger = useCallback(() => api("/chatbot/evaluation").then(setD).catch((e) => setErreur(e.message)), []);
  useEffect(() => { charger(); }, [charger]);
  const ev = d?.evaluations?.[0];
  useEffect(() => {
    if (ev?.statut !== "en_cours") return undefined;
    const t = setInterval(charger, 5000);
    return () => clearInterval(t);
  }, [ev?.statut, charger]);
  const lancer = async () => {
    if (!window.confirm("Lancer l'évaluation ? Environ 50 questions, quelques minutes, environ 1 à 2 $ de crédit Anthropic.")) return;
    setErreur("");
    try { await api("/chatbot/evaluation", { method: "POST", body: JSON.stringify({ consignes: "enregistrees" }) }); charger(); }
    catch (e) { setErreur(e.message); }
  };
  const res = (ev?.resultats || []).slice().sort((a, b) => a.n - b.n);
  const compte = (v) => res.filter((x) => x.verdict === v).length;
  const lignes = filtre ? res.filter((x) => (filtre === "a_revoir" ? x.verdict !== "juste" : x.categorie.startsWith(filtre))) : res;
  const categories = [...new Set(res.map((x) => x.categorie.split(" — ")[0]))];
  return (
    <div className="space-y-5" data-testid="assistant-evaluation">
      <div className="flex flex-wrap items-center gap-3">
        <BoutonSombre onClick={lancer} disabled={ev?.statut === "en_cours"} testid="assistant-evaluation-lancer">
          {ev?.statut === "en_cours" ? `En cours… ${ev.faites}/${ev.total}` : "Lancer l'évaluation"}
        </BoutonSombre>
        <span className="text-xs text-[var(--muted)]">
          Environ 50 questions de familles. Le planning est vérifié par le calcul ; le reste est relu par un modèle plus fort, avec tout le site.
        </span>
      </div>
      {erreur && <p className="text-sm text-red-700">{erreur}</p>}
      {ev?.statut === "interrompue" && (
        <p className="text-sm" style={{ color: "#B45309" }}>
          Cette évaluation a été interrompue à {ev.faites}/{ev.total} (le serveur a redémarré, par exemple
          après une mise en ligne). Relance-la : les résultats ci-dessous sont partiels.
        </p>
      )}
      {!ev && <p className="text-sm text-[var(--muted)]">Aucune évaluation pour l'instant.</p>}
      {ev && (
        <>
          <p className="text-xs text-[var(--muted)]">
            {heure(ev.lancee_le)} · {ev.modele} · {ev.consignes}{ev.cout ? ` · coût ${dollars(ev.cout)}` : ""}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Carte titre="Questions" valeur={`${res.length}${ev.statut === "en_cours" ? ` / ${ev.total}` : ""}`} />
            <Carte titre="Justes" valeur={compte("juste")} ton="vert" />
            <Carte titre="Partielles" valeur={compte("partiel")} ton="ambre" />
            <Carte titre="Fausses" valeur={compte("faux") + compte("erreur")} ton={compte("faux") + compte("erreur") ? "rouge" : "neutre"} />
          </div>
          <Puces valeur={filtre} onChange={setFiltre} options={[
            { valeur: "", label: "Toutes", nb: res.length },
            { valeur: "a_revoir", label: "À revoir", nb: res.length - compte("juste") },
            ...categories.map((c) => ({ valeur: c, label: c, nb: res.filter((x) => x.categorie.startsWith(c)).length })),
          ]} />
          <Tableau
            entetes={["#", "Catégorie", { label: "Question", large: true }, "Résultat"]}
            lignes={lignes} onClic={setOuverte} vide="Rien ici."
            rendu={(x) => (
              <>
                <Cellule muet>{x.n}</Cellule>
                <Cellule muet>{x.categorie}</Cellule>
                <Cellule>{x.question}<div className="text-xs text-[var(--muted)] mt-0.5">{x.explication}</div></Cellule>
                <Cellule><Badge ton={(VERDICTS[x.verdict] || VERDICTS.erreur).ton}>{(VERDICTS[x.verdict] || VERDICTS.erreur).label}</Badge></Cellule>
              </>
            )}
          />
        </>
      )}
      {ouverte && (
        <Modale titre={`Question ${ouverte.n}`} sous_titre={ouverte.categorie} onFermer={() => setOuverte(null)} large>
          <p className="font-semibold text-sm mb-3">{ouverte.question}</p>
          <div className="rounded-2xl px-4 py-3 text-sm mb-4" style={{ background: "#F1F5F9" }}>
            <TexteAssistant texte={ouverte.reponse || "(pas de réponse)"} nouvelOnglet />
          </div>
          <p className="text-sm mb-2"><Badge ton={(VERDICTS[ouverte.verdict] || VERDICTS.erreur).ton}>{(VERDICTS[ouverte.verdict] || VERDICTS.erreur).label}</Badge> {ouverte.explication}</p>
          {ouverte.amelioration && <p className="text-sm text-[var(--muted)]">À améliorer : {ouverte.amelioration}</p>}
          {Array.isArray(ouverte.attendu) ? (
            <details className="text-xs mt-3"><summary className="cursor-pointer">Cours attendus ({ouverte.attendu.length})</summary>
              <ul className="mt-1 space-y-0.5">{ouverte.attendu.map((a) => <li key={a}>{a}</li>)}</ul></details>
          ) : ouverte.attendu && <p className="text-xs text-[var(--muted)] mt-2">Attendu : {ouverte.attendu}</p>}
        </Modale>
      )}
    </div>
  );
}

/* Comment l'assistant lit chaque source. */
const LECTURES = {
  direct: { label: "En direct", fond: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D", aide: "Relu dans la console à chaque question : toujours à jour." },
  toujours: { label: "À chaque question", fond: "#EFF6FF", bord: "#BFDBFE", texte: "#1D4ED8", aide: "Sous ses yeux avant chaque réponse." },
  instagram: { label: "Instagram seulement", fond: "#FDF2F8", bord: "#FBCFE8", texte: "#BE185D", aide: "Ajouté seulement pour les messages privés Instagram." },
  besoin: { label: "Au besoin", fond: "#F8FAFC", bord: "#E2E8F0", texte: "#475569", aide: "Il ouvre la page lui-même quand la question le demande." },
};
const PastilleLecture = ({ lecture }) => {
  const l = LECTURES[lecture] || LECTURES.besoin;
  return (
    <span className="inline-flex text-[10.5px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap"
      style={{ background: l.fond, borderColor: l.bord, color: l.texte }} title={l.aide}>{l.label}</span>
  );
};
const taille = (n) => `${(Math.max(100, Math.round(n / 100) * 100)).toLocaleString("fr-CH")} caractères`;

/* Une source : son titre (lien vers la page du site), comment il la lit, une ligne de résumé ;
   son texte brut s'ouvre à la demande. */
function SourceSavoir({ f }) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState(null);
  const basculer = () => {
    setOuvert(!ouvert);
    if (texte === null) {
      api(`/chatbot/connaissances/source?id=${encodeURIComponent(f.id)}`)
        .then((r) => setTexte(r.texte)).catch((e) => setTexte(`(${e.message})`));
    }
  };
  return (
    <li className="py-3 border-t first:border-t-0" style={{ borderColor: "var(--border-soft)" }} data-testid="assistant-savoir-source">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {f.url ? (
          <a href={f.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:underline inline-flex items-center gap-1">
            {f.titre}<ExternalLink size={12} className="text-[var(--muted)]" />
          </a>
        ) : <span className="font-semibold text-sm">{f.titre}</span>}
        <PastilleLecture lecture={f.lecture} />
        <span className="text-[11px] text-[var(--muted)] ml-auto whitespace-nowrap">{taille(f.caracteres)}</span>
        <button type="button" onClick={basculer} className="text-xs underline text-[var(--muted)] whitespace-nowrap"
          data-testid="assistant-savoir-voir">{ouvert ? "Masquer le texte" : "Voir le texte"}</button>
      </div>
      {f.resume && <p className="text-xs text-[var(--muted)] mt-0.5 line-clamp-2">{f.resume}</p>}
      {ouvert && (
        <pre className="whitespace-pre-wrap text-xs mt-2 p-3 rounded-xl border bg-white max-h-[50vh] overflow-auto"
          style={{ borderColor: "var(--border)", overflowWrap: "anywhere" }}>{texte ?? "Chargement…"}</pre>
      )}
    </li>
  );
}

/* Une rubrique. Une longue liste de pages « au besoin » (les 22 pages des cours) reste repliée
   après les premières, pour que l'écran se lise d'un coup d'œil. */
const VISIBLES_AU_BESOIN = 5;
function GroupeSavoir({ g }) {
  const [tout, setTout] = useState(false);
  const auBesoin = g.sources.filter((f) => f.lecture === "besoin");
  const caches = tout ? 0 : Math.max(0, auBesoin.length - VISIBLES_AU_BESOIN);
  const montrees = caches ? g.sources.filter((f) => f.lecture !== "besoin").concat(auBesoin.slice(0, VISIBLES_AU_BESOIN)) : g.sources;
  return (
    <Section titre={g.titre} aide={g.aide} testid={`assistant-savoir-${g.id}`}
      action={<span className="text-xs text-[var(--muted)]">{g.sources.length} source{g.sources.length > 1 ? "s" : ""}</span>}>
      <ul className="bg-white rounded-2xl border px-4" style={{ borderColor: "var(--border)" }}>
        {montrees.map((f) => <SourceSavoir key={f.id} f={f} />)}
        {caches > 0 && (
          <li className="py-3 border-t" style={{ borderColor: "var(--border-soft)" }}>
            <button type="button" className="text-sm font-semibold" style={{ color: "#007E85" }} onClick={() => setTout(true)}
              data-testid="assistant-savoir-plus">
              Voir les {caches} autres pages
            </button>
          </li>
        )}
      </ul>
    </Section>
  );
}

/* « Ce qu'il sait » : la liste de ce que l'assistant lit, rangée par rubrique, au lieu d'un
   seul gros texte (école, 09.10.2026). Le texte complet reste visible, replié. */
function Savoir() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [complet, setComplet] = useState(null);
  useEffect(() => { api("/chatbot/connaissances").then(setD).catch((e) => setErreur(e.message)); }, []);
  if (erreur) return <p className="text-sm text-red-700">{erreur}</p>;
  if (!d) return <p className="text-sm text-[var(--muted)]">Chargement…</p>;
  const chargerComplet = (e) => {
    if (e.target.open && complet === null) {
      api("/chatbot/connaissances?texte=true").then((r) => setComplet(r.texte)).catch((x) => setComplet(`(${x.message})`));
    }
  };
  return (
    <div className="space-y-6" data-testid="assistant-savoir">
      <div className="space-y-3 max-w-3xl">
        <p className="text-sm">
          Avant chaque réponse, l'assistant lit ses consignes, le <b>planning complet{d.lignes_planning ? ` (${d.lignes_planning} cours)` : ""}</b> et
          les tarifs et dates des Réglages, en direct, et <b>{d.pages_toujours} pages du site</b>. Les <b>{d.pages_besoin} autres
          pages</b>, il les ouvre lui-même quand une question le demande. Rien d'autre : ni élèves, ni familles, ni factures.
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
          {Object.entries(LECTURES).map(([k, l]) => (
            <span key={k} className="inline-flex items-center gap-1.5"><PastilleLecture lecture={k} /> {l.aide}</span>
          ))}
        </div>
      </div>
      {d.groupes.map((g) => <GroupeSavoir key={g.id} g={g} />)}
      <details className="text-sm" onToggle={chargerComplet} data-testid="assistant-savoir-complet">
        <summary className="cursor-pointer text-[var(--muted)]">
          Tout le texte, exactement comme l'assistant le lit avant chaque réponse ({nombre(d.caracteres)} caractères)
        </summary>
        <pre className="whitespace-pre-wrap text-xs mt-2 p-4 rounded-2xl border bg-white max-h-[70vh] overflow-auto"
          style={{ borderColor: "var(--border)", overflowWrap: "anywhere" }}>{complet ?? "Chargement…"}</pre>
      </details>
    </div>
  );
}
